'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import styles from './CreateWalletConfirm.module.css'

import RemoveIcon from '@/icons/remove'
import SuccessIcon from '@/icons/success'
import CreateWalletTitle from '../create-wallet-title'
import CreateWalletHeader from '../create-wallet-header'
import CreateWalletFooter from '../create-wallet-footer'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

/** 1-based positions the user must reproduce. */
export const CHECKED_POSITIONS = [3, 6, 9, 12]

function hashSeed(seed: string) {
	let hash = 2166136261
	for (let i = 0; i < seed.length; i++) {
		hash ^= seed.charCodeAt(i)
		hash = Math.imul(hash, 16777619)
	}
	return hash >>> 0
}

function nextSeededValue(seed: number) {
	return Math.imul(seed ^ (seed >>> 15), 1 | seed) >>> 0
}

/** Deterministic per phrase so a re-render never reshuffles the bank. */
function shuffleIndexes(count: number, seedPhrase: string) {
	const shuffled = Array.from({ length: count }, (_, i) => i)
	let seed = hashSeed(seedPhrase)
	for (let i = shuffled.length - 1; i > 0; i--) {
		seed = nextSeededValue(seed + i)
		const j = seed % (i + 1)
		const tmp = shuffled[i]
		shuffled[i] = shuffled[j]
		shuffled[j] = tmp
	}
	return shuffled
}

/**
 * The backup check. Selection is tracked by word index rather than word text
 * so a phrase with a repeated word (BIP39 allows it) still works: each bank
 * chip is one slot of the phrase, and a chip disappears only when that chip
 * was picked.
 */
const CreateWalletConfirm: FC<{
	step: number
	seed: string
	totalSteps: number
	/** Called once all checked words are correct. May be async; errors are shown. */
	onConfirm: (evt: any) => void | Promise<void>
	onBack: (evt: any) => void
}> = ({ step, seed, totalSteps, onConfirm, onBack }): ReactElement => {
	const words = React.useMemo(() => seed.trim().split(/\s+/), [seed])
	const [selected, setSelected] = React.useState<(number | null)[]>(() =>
		CHECKED_POSITIONS.map(() => null)
	)
	const [submitting, setSubmitting] = React.useState(false)
	const [error, setError] = React.useState('')

	const bankOrder = React.useMemo(() => shuffleIndexes(words.length, seed), [words.length, seed])

	const remainingBank = React.useMemo(
		() => bankOrder.filter((wordIndex) => !selected.includes(wordIndex)),
		[bankOrder, selected]
	)

	const isSuccess = React.useMemo(
		() => selected.every((wordIndex, slot) => wordIndex === CHECKED_POSITIONS[slot] - 1),
		[selected]
	)

	const handleSelect = React.useCallback((wordIndex: number) => {
		setSelected((prev) => {
			const slot = prev.findIndex((e) => e === null)
			if (slot < 0 || prev.includes(wordIndex)) {
				return prev
			}
			const nextSelected = [...prev]
			nextSelected[slot] = wordIndex
			return nextSelected
		})
	}, [])

	const handleRemove = React.useCallback((slot: number) => {
		setSelected((prev) => {
			const nextSelected = [...prev]
			nextSelected[slot] = null
			return nextSelected
		})
	}, [])

	const handleSubmit = React.useCallback(
		async (evt: any) => {
			evt?.preventDefault?.()
			if (!isSuccess || submitting) {
				return
			}
			setSubmitting(true)
			setError('')
			try {
				await onConfirm(evt)
			} catch (_e) {
				setError('An error occurred. Please try again.')
			} finally {
				setSubmitting(false)
			}
		},
		[onConfirm, isSuccess, submitting]
	)

	const renderSlot = (wordIndex: number | null, slot: number) => {
		const position = CHECKED_POSITIONS[slot]
		const word = wordIndex === null ? '' : words[wordIndex]
		const valid = wordIndex === position - 1

		return (
			<div
				translate="no"
				className={styles.word}
				key={position}
				data-testid={`confirm-slot-${position}`}
				data-state={wordIndex === null ? 'empty' : valid ? 'valid' : 'invalid'}
			>
				<p className={classNames(styles.wordNumber, 'text-md', 'regular')}>{position}</p>
				<p
					className={classNames(styles.wordLabel, 'text-md', 'regular', {
						[styles.wordLabelSuccess]: wordIndex !== null && valid,
						[styles.wordLabelError]: wordIndex !== null && !valid
					})}
				>
					{word}
					{wordIndex !== null && (
						<RemoveIcon className={styles.removeWordIcon} onClick={() => handleRemove(slot)} />
					)}
				</p>
			</div>
		)
	}

	const renderBankWord = (wordIndex: number) => {
		const word = words[wordIndex]
		return (
			<div
				translate="no"
				data-testid={`confirm-bank-word-${word}`}
				className={styles.selectWord}
				onClick={() => handleSelect(wordIndex)}
				key={wordIndex}
				role="button"
				tabIndex={0}
				onKeyDown={onKeyboardClick(() => handleSelect(wordIndex))}
			>
				<p className={classNames(styles.selectWordLabel, 'text-md', 'regular')}>{word}</p>
			</div>
		)
	}

	const nextDisabled = !isSuccess || submitting

	return (
		<>
			<CreateWalletHeader
				step={step}
				total={totalSteps}
				nextDisabled={nextDisabled}
				onNext={handleSubmit}
				onBack={onBack}
			/>
			<div className={styles.root}>
				<CreateWalletTitle step={step} />
				<div className={styles.grid}>{selected.map(renderSlot)}</div>
				<div className={styles.selectWords}>
					{!isSuccess && remainingBank.map(renderBankWord)}
					{isSuccess && (
						<p
							data-testid="confirm-success"
							className={classNames(styles.success, 'text-md', 'regular')}
						>
							<SuccessIcon className={styles.successIcon} />
							Success!
						</p>
					)}
				</div>
				{error && (
					<p role="alert" className={classNames(styles.error, 'text-sm', 'regular')}>
						{error}
					</p>
				)}
			</div>
			<CreateWalletFooter
				step={step}
				nextDisabled={nextDisabled}
				onNext={handleSubmit}
				onBack={onBack}
				loading={submitting}
			/>
		</>
	)
}

export default CreateWalletConfirm
